import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTA from "@/components/CTA";
import Seo, { productSchema } from "@/components/Seo";
import { Check, CaretRight } from "@phosphor-icons/react";
import { trackViewSystem } from "@/lib/analytics";

export function SystemsIndex() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/systems").then((r) => setItems(r.data)); }, []);
  return (
    <div data-testid="systems-index">
      <Seo title="Solar Surveillance Systems — Mini, Standard, Pro" description="Compare our three solar surveillance configurations: pole-mounted Mini, 360° Standard trailer, and PTZ-equipped Pro trailer. Choose the right coverage for your site." path="/systems" />
      <section className="container-x pt-10 pb-6"><Breadcrumbs items={[{ label: "Systems" }]} /></section>
      <section className="container-x pb-10">
        <div className="eyebrow text-solar mb-3">Systems</div>
        <h1 className="mb-4">Mini · Standard · Pro</h1>
        <p className="text-white/75 text-lg max-w-2xl">
          Three configurations covering pole-mounted focused coverage, trailer-mounted 360° coverage and high-risk sites requiring PTZ and active response.
        </p>
        <div className="mt-6">
          <Link to="/systems/compare" data-testid="systems-compare-cta" className="btn-solar inline-block">Compare All Systems</Link>
        </div>
      </section>
      <section className="container-x pb-20 grid md:grid-cols-3 gap-px bg-chrome border border-chrome">
        {items.map((s) => (
          <Link key={s.slug} to={`/systems/${s.slug}`} className="bg-obsidian p-8 hover:bg-steel transition-colors group">
            <div className="eyebrow text-solar mb-2">{s.name}</div>
            <div className="font-display uppercase text-2xl font-bold mb-3 group-hover:text-solar transition-colors">{s.short_name}</div>
            <p className="text-white/70 text-sm leading-relaxed">{s.summary}</p>
          </Link>
        ))}
      </section>
      <CTA />
    </div>
  );
}

export function SystemDetail() {
  const { slug } = useParams();
  const [sys, setSys] = useState(null);
  useEffect(() => {
    api.get(`/systems/${slug}`).then((r) => {
      setSys(r.data);
      trackViewSystem(r.data);
    }).catch(() => setSys(false));
  }, [slug]);
  if (sys === false) return <div className="container-x py-20"><h1>System not found</h1></div>;
  if (!sys) return <div className="container-x py-20 text-white/60">Loading…</div>;
  return (
    <div data-testid={`system-detail-${slug}`}>
      <Seo
        title={sys.title}
        description={sys.hero_statement || sys.summary}
        path={`/systems/${slug}`}
        ogImage={sys.image}
        schema={productSchema(sys)}
        breadcrumb={[{ label: "Home", path: "/" }, { label: "Systems", path: "/systems" }, { label: sys.short_name }]}
      />
      <section className="container-x pt-10 pb-6"><Breadcrumbs items={[{ label: "Systems", to: "/systems" }, { label: sys.short_name }]} /></section>
      <section className="container-x pb-16 grid lg:grid-cols-[1.2fr_1fr] gap-10">
        <div>
          <div className="eyebrow text-solar mb-3">{sys.name} System</div>
          <h1 className="mb-5">{sys.title}</h1>
          <p className="text-white/80 text-lg leading-relaxed mb-6">{sys.hero_statement}</p>
          <p className="text-white/70 leading-relaxed mb-6">{sys.summary}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/request-a-quote" data-testid="system-detail-quote-cta" className="btn-solar inline-block text-center">Request Assessment</Link>
            <Link to="/systems/compare" className="btn-ghost inline-block text-center">Compare Systems</Link>
          </div>
        </div>
        <div className="border border-chrome overflow-hidden bg-steel">
          <img src={sys.image} alt={sys.title} className="w-full aspect-[4/3] object-cover" />
        </div>
      </section>

      <section className="bg-steel py-16 border-y border-chrome">
        <div className="container-x grid md:grid-cols-2 gap-10">
          <div>
            <div className="eyebrow text-solar mb-3">Key Features</div>
            <ul className="space-y-3">
              {(sys.features || []).map((f) => (
                <li key={f} className="flex items-start gap-3 text-white/85">
                  <Check size={18} weight="bold" className="text-solar mt-1 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="eyebrow text-solar mb-3">Best Uses</div>
            <ul className="space-y-3">
              {(sys.best_uses || []).map((b) => (
                <li key={b} className="flex items-start gap-3 text-white/85">
                  <CaretRight size={18} weight="bold" className="text-solar mt-1 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="container-x py-16 grid md:grid-cols-3 gap-px bg-chrome border border-chrome">
        <InfoBlock title="Deployment Requirements" body={sys.deployment_requirements} />
        <InfoBlock title="Monitoring Options" body={sys.monitoring_options} />
        <InfoBlock title="Storage" body={sys.storage_details} />
      </section>

      <CTA />
    </div>
  );
}

function InfoBlock({ title, body }) {
  return (
    <div className="bg-obsidian p-8">
      <div className="eyebrow text-solar mb-3">{title}</div>
      <div className="text-white/80 leading-relaxed text-sm">{body}</div>
    </div>
  );
}

export function CompareSystems() {
  const [systems, setSystems] = useState([]);
  useEffect(() => { api.get("/systems").then((r) => setSystems(r.data)); }, []);
  const featureUnion = Array.from(new Set(systems.flatMap((s) => s.features || [])));
  return (
    <div data-testid="compare-systems">
      <Seo title="Compare Mini, Standard and Pro Surveillance Systems" description="Side-by-side comparison of all three 360 View solar surveillance systems: features, best-use cases, deployment requirements." path="/systems/compare" />
      <section className="container-x pt-10 pb-6"><Breadcrumbs items={[{ label: "Systems", to: "/systems" }, { label: "Compare" }]} /></section>
      <section className="container-x pb-8">
        <div className="eyebrow text-solar mb-3">Compare</div>
        <h1 className="mb-4">All three systems, side by side.</h1>
        <p className="text-white/75 max-w-2xl">Coverage varies by site — a site assessment determines the right configuration and unit count.</p>
      </section>
      <section className="container-x pb-20 overflow-x-auto">
        <table className="w-full border border-chrome min-w-[720px]">
          <thead>
            <tr className="bg-steel">
              <th className="text-left p-4 text-xs uppercase tracking-widest text-white/60 border-r border-chrome min-w-[220px]">Feature</th>
              {systems.map((s) => (
                <th key={s.slug} className="text-left p-4 border-r last:border-r-0 border-chrome">
                  <div className="eyebrow text-solar">{s.name}</div>
                  <div className="font-display uppercase text-lg font-bold">{s.short_name}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {featureUnion.map((feat) => (
              <tr key={feat} className="border-t border-chrome">
                <td className="p-4 text-sm text-white/80 border-r border-chrome">{feat}</td>
                {systems.map((s) => (
                  <td key={s.slug} className="p-4 text-center border-r last:border-r-0 border-chrome">
                    {(s.features || []).includes(feat) ? (
                      <Check size={20} weight="bold" className="text-solar inline" />
                    ) : (
                      <span className="text-white/25">—</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
            <tr className="border-t border-chrome bg-steel/40">
              <td className="p-4"></td>
              {systems.map((s) => (
                <td key={s.slug} className="p-4">
                  <Link to={`/systems/${s.slug}`} className="btn-ghost inline-block text-sm">View {s.name}</Link>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </section>
      <CTA />
    </div>
  );
}
